import { useTranslation } from 'react-i18next';
import { LockErrorItemLabel } from '@/accessControl/LockErrorItemLabel.atom';
import { ChildRelationshipEnum } from '@seeqdev/api/model/LockErrorPathOutputV1';
import { faExclamationTriangle } from '@seeqdev/icons/fa-sharp-regular-svg-icons';
import { Icon } from '@seeqdev/qomponents';
import type { SharedGroupReason } from '@/accessControl/groupLockErrorsByWorkbook';
import type { LockErrorOutputV1 } from '@seeqdev/api/model/LockErrorOutputV1';

/**
 * The sentence a group states once when every one of its errors failed for the same reason (`getGroupSharedReason`).
 * Workbook groups always say "lock this workbook first"; the no-workbook cases need copy per relationship, and only
 * the relationships listed in `GLOBAL_BUCKET_SUMMARIZABLE_RELATIONSHIPS` ever reach here without a workbook.
 */
const getSharedReasonText = (
  t: (key: string) => string,
  sharedReason: SharedGroupReason,
  hasWorkbook: boolean,
  isVantage: boolean,
): string => {
  if (hasWorkbook) {
    /*
     * TODO CRAB-62713: provisional copy, pending a copy review - move to en.json once the wording is settled. The
     * group header already names the workbook and shows its UNLOCKED badge, so this sentence deliberately doesn't
     * repeat either.
     */
    return isVantage
      ? 'Lock this workbook before locking the Vantage Room. These items are referenced from it:'
      : 'Lock this workbook before locking this Analysis. These items are referenced from it:';
  }
  if (sharedReason.childRelationship === ChildRelationshipEnum.MONITORINGSOURCENOTLOCKED) {
    return t('ACCESS_CONTROL.LOCKING.GROUPED_BY_WORKBOOK.OUTSIDE_WORKBOOKS_MONITORING_SOURCE_NOT_LOCKED_VANTAGE');
  }
  // Unreachable while GLOBAL_BUCKET_SUMMARIZABLE_RELATIONSHIPS only lists the case above; keep the type checker honest.
  return t(`ACCESS_CONTROL.LOCKING.ERROR_REASONS.${sharedReason.childRelationship}`);
};

/**
 * A group's single shared failure reason, stated once above a plain list of the items it applies to, instead of the
 * same sentence repeated under every item. Used inside a workbook group's accordion, inside the
 * "Items outside these workbooks" bucket, and for the ungrouped flat list when everything in it failed identically.
 */
export const LockErrorSharedReasonSummary = ({
  errors,
  sharedReason,
  hasWorkbook,
  isVantage,
  testId,
}: {
  errors: LockErrorOutputV1[];
  sharedReason: SharedGroupReason;
  hasWorkbook: boolean;
  isVantage: boolean;
  testId: string;
}) => {
  const { t } = useTranslation();

  return (
    <div data-testid={testId}>
      <div className="tw:mb-sq-10">
        <Icon
          icon={faExclamationTriangle}
          large={true}
          type="danger"
          testId="workbook-group-shared-reason"
          extraClassNames="tw:mr-sq-5"
        />
        {getSharedReasonText(t, sharedReason, hasWorkbook, isVantage)}
      </div>
      <div className="tw:ml-sq-10">
        {errors.map((globalItem) => (
          <div key={`error-item-${globalItem.item.id}`} className="tw:mb-0.5">
            <LockErrorItemLabel item={globalItem.item} itemName={globalItem.item.name} />
          </div>
        ))}
      </div>
    </div>
  );
};
